import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { useLocalStorage } from './useLocalStorage';
import lightTheme from './themes/light';
import darkTheme from './themes/dark';

const ThemeContext = createContext();

const THEMES = {
  light: lightTheme,
  dark: darkTheme,
};

const applyStatusBarColor = (color) => {
  const metaThemeColor = document.querySelector(
    'meta[name="theme-color"]'
  );

  if (metaThemeColor && color) {
    metaThemeColor.setAttribute(
      'content',
      color
    );
  }
};

export function ThemeProvider({ children }) {
  const [systemTheme, setSystemTheme] = useState(
    window.matchMedia(
      '(prefers-color-scheme: dark)'
    ).matches
      ? 'dark'
      : 'light'
  );

  const [theme, setTheme] = useLocalStorage(
    'theme',
    'system'
  );

  const resolvedTheme =
    theme === 'system'
      ? systemTheme
      : theme;

  const colors = THEMES[resolvedTheme];

  const toggleTheme = () => {
    setTheme((prevTheme) =>
      prevTheme === 'light'
        ? 'dark'
        : 'light'
    );
  };

  const setStatusBarColor = useCallback(
    (color) => {
      applyStatusBarColor(color);
    },
    []
  );

  const restoreStatusBarColor = useCallback(
    () => {
      applyStatusBarColor(
        THEMES[resolvedTheme].backgroundColor
      );
    },
    [resolvedTheme]
  );

  useEffect(() => {
    const matchMedia = window.matchMedia(
      '(prefers-color-scheme: dark)'
    );

    const updateSystemTheme = (event) => {
      setSystemTheme(
        event.matches
          ? 'dark'
          : 'light'
      );
    };

    matchMedia.addEventListener(
      'change',
      updateSystemTheme
    );

    return () => {
      matchMedia.removeEventListener(
        'change',
        updateSystemTheme
      );
    };
  }, []);

  useEffect(() => {
    const root = document.documentElement;

    /*
     * Não usar body.className =
     * porque isso apagaria nf-overlay-open
     * e qualquer outra classe temporária.
     */
    document.body.classList.remove(
      'light',
      'dark',
      'system-light',
      'system-dark'
    );

    document.body.classList.add(
      resolvedTheme
    );

    if (theme === 'system') {
      document.body.classList.add(
        `system-${systemTheme}`
      );
    }

    root.dataset.theme = resolvedTheme;

    Object.keys(colors).forEach((variable) => {
      root.style.setProperty(
        `--${variable}`,
        colors[variable]
      );
    });

    applyStatusBarColor(
      colors.backgroundColor
    );

    document.body.style.backgroundColor =
      colors.backgroundColor;
  }, [
    theme,
    systemTheme,
    resolvedTheme,
    colors,
  ]);

  const contextValue = {
    theme,
    resolvedTheme,
    systemTheme,
    toggleTheme,
    setTheme,
    colors,
    setStatusBarColor,
    restoreStatusBarColor,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}